import { useState, useEffect } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import { Box, Drawer, Tooltip, IconButton, Button, Typography, Link, useMediaQuery, useTheme, Collapse, Fade, ButtonBase } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import Avatar from '../../components/ui/Avatar';
import { logout } from '../../redux/actions/authActions';
import { fetchCategories } from '../../redux/actions/gameActions';
import Logo from '../../components/Logo';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import Iconify from '../../components/Iconify';
import { getNavConfig } from './NavConfig';
import { RED } from '../../utils/brandColors';
import { displayHandle } from '../../utils/displayHandle';
import { DOMAIN } from '../../redux/constants';

export const DRAWER_WIDTH = 240;
export const COLLAPSED_DRAWER_WIDTH = 72;

const RootStyle = styled('div', { shouldForwardProp: (prop) => prop !== 'collapsed' })(({ theme, collapsed }) => ({
  flexShrink: 0,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  // Only take space on desktop — mobile uses BottomNav
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
  [theme.breakpoints.up('lg')]: {
    width: DRAWER_WIDTH,
  },
}));

export default function DashboardSidebar({ showCategories = false, visibleCategory = null }) {
  const { userInfo } = useSelector((state) => state.userLogin);
  const dispatch = useDispatch();
  const nav = getNavConfig(userInfo);
  const theme = useTheme();
  const isCollapsed = false;
  const drawerWidth = DRAWER_WIDTH;

  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    dispatch(fetchCategories()).then((cats) => {
      if (cats) setCategories(cats);
    });
  }, [dispatch]);

  const getSidebarCategories = () => {
    const list = [{ name: 'All', slug: 'all' }];
    if (userInfo && !userInfo.isAnon) {
      list.push({ name: 'Following', slug: 'following' });
    }
    categories.forEach((c) => {
      list.push({ name: c.name, slug: c.slug });
    });
    return list;
  };

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { minHeight: '100%', display: 'flex', flexDirection: 'column', pb: 2 },
      }}
    >
      {/* Logo */}
      <Box sx={{ px: isCollapsed ? 1.5 : 2.5, pt: 3, pb: 3, display: 'flex', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
        <Logo mini={isCollapsed} />
      </Box>

      {/* Nav links & Categories transition container */}
      {!isCollapsed ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '100%',
            gridTemplateRows: '100%',
            width: '100%',
            '& > *': {
              gridArea: '1 / 1 / 2 / 2',
            },
          }}
        >
          <Fade in={!showCategories} timeout={250} mountOnEnter unmountOnExit>
            <Box sx={{ width: '100%' }}>
              <NavSection navConfig={nav} mini={isCollapsed} />
            </Box>
          </Fade>
          <Fade in={showCategories} timeout={250} mountOnEnter unmountOnExit>
            <Box sx={{ width: '100%', px: 2.5, mt: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'text.disabled',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  letterSpacing: 1.2,
                  display: 'block',
                  mb: 1,
                }}
              >
                Categories
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  maxHeight: '280px',
                  overflowY: 'auto',
                  pr: 0.5,
                  // Smooth custom scrollbar
                  '&::-webkit-scrollbar': { width: 4 },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'divider',
                    borderRadius: 2,
                  },
                }}
              >
                {getSidebarCategories().map((cat) => {
                  const currentCategory = searchParams.get('category') || 'all';
                  const isActive = visibleCategory ? visibleCategory === cat.slug : currentCategory === cat.slug;

                  return (
                    <ButtonBase
                      key={cat.slug}
                      component={RouterLink}
                      to={cat.slug === 'all' ? '/' : `/?category=${cat.slug}`}
                      sx={{
                        width: '100%',
                        justifyContent: 'flex-start',
                        px: 2,
                        py: 0.8,
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        fontWeight: isActive ? 700 : 500,
                        transition: 'all 0.2s ease',
                        bgcolor: isActive ? alpha(RED, 0.08) : 'transparent',
                        color: isActive ? RED : 'text.secondary',
                        '&:hover': {
                          bgcolor: isActive ? alpha(RED, 0.08) : 'action.hover',
                          color: isActive ? RED : 'text.primary',
                        },
                      }}
                    >
                      {cat.name}
                    </ButtonBase>
                  );
                })}
              </Box>
            </Box>
          </Fade>
        </Box>
      ) : (
        <NavSection navConfig={nav} mini={isCollapsed} />
      )}

      <Box sx={{ flexGrow: 1 }} />

      {/* Bottom row: three modes.
          - Claimed user: avatar → profile, logout icon
          - Anon (guest): handle + "Sign up" CTA → /claim, no logout
          - Unauthenticated: "Log in" CTA */}
      {userInfo && !userInfo.isAnon && (
        isCollapsed ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 3, pt: 1.5 }}>
            <Tooltip title="Profile" placement="right" arrow>
              <Box component={RouterLink} to="/u/me" sx={{ display: 'flex', textDecoration: 'none' }}>
                <Avatar
                  src={userInfo.avatarUrl || undefined}
                  name={userInfo.username}
                  sx={{ width: 32, height: 32, bgcolor: RED, fontSize: '0.85rem' }}
                />
              </Box>
            </Tooltip>
            <Tooltip title="Log out" placement="right" arrow>
              <IconButton
                onClick={() => dispatch(logout())}
                size="small"
                aria-label="Log out"
                sx={{
                  color: 'text.secondary',
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    color: RED, 
                    bgcolor: alpha(RED, 0.06) 
                  }
                }}
              >
                <Iconify icon="eva:log-out-outline" sx={{ width: 20, height: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ mx: 1.5, mb: 2, pt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component={RouterLink}
              to="/u/me"
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                flex: 1, minWidth: 0,
                textDecoration: 'none', color: 'inherit',
                borderRadius: 2, px: 0.5, py: 0.5,
                '&:hover': { bgcolor: alpha(RED, 0.06) },
              }}
            >
              <Avatar
                src={userInfo.avatarUrl || undefined}
                name={userInfo.username}
                sx={{ width: 32, height: 32, bgcolor: RED, fontSize: '0.85rem' }}
              />
              <Typography variant="body2" noWrap sx={{ fontWeight: 700, color: 'text.primary', minWidth: 0 }}>
                {displayHandle(userInfo)}
              </Typography>
            </Box>
            <Tooltip title="Log out" arrow>
              <IconButton
                onClick={() => dispatch(logout())}
                size="small"
                aria-label="Log out"
                sx={{
                  color: 'text.secondary',
                  borderRadius: 2,
                  flexShrink: 0,
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    color: RED, 
                    bgcolor: alpha(RED, 0.06) 
                  }
                }}
              >
                <Iconify icon="eva:log-out-outline" sx={{ width: 20, height: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )
      )}

      {(!userInfo || userInfo.isAnon) && (
        isCollapsed ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 3 }}>
            <Tooltip title="Sign up" placement="right" arrow>
              <IconButton
                component={RouterLink}
                to="/register"
                sx={{
                  color: '#fff',
                  bgcolor: RED,
                  width: 36,
                  height: 36,
                  '&:hover': { bgcolor: alpha(RED, 0.9) },
                }}
              >
                <Iconify icon="eva:person-add-outline" sx={{ width: 20, height: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Log in" placement="right" arrow>
              <IconButton
                component={RouterLink}
                to="/login"
                sx={{
                  color: 'text.primary',
                  border: '1px solid',
                  borderColor: 'divider',
                  width: 36,
                  height: 36,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Iconify icon="eva:log-in-outline" sx={{ width: 20, height: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ px: 1.5, pb: 3, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Button
              component={RouterLink}
              to="/register"
              fullWidth
              variant="contained"
              color="primary"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                py: 1,
                bgcolor: RED,
                color: '#fff',
                '&:hover': { bgcolor: alpha(RED, 0.9) },
              }}
            >
              Sign up
            </Button>
            <Button
              component={RouterLink}
              to="/login"
              fullWidth
              variant="outlined"
              color="primary"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                py: 1,
                borderColor: 'divider',
                color: 'text.primary',
                bgcolor: 'transparent',
                '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' },
              }}
            >
              Log in
            </Button>
          </Box>
        )
      )}

      {/* Brand Footer */}
      {!isCollapsed && (
        <Box sx={{ px: 2.5, pb: 2, pt: 1, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 0.5 }}>
            <Link
              component={RouterLink}
              to="/legal/about"
              sx={{ fontSize: '0.72rem', color: 'text.secondary', textDecoration: 'none', '&:hover': { color: RED, textDecoration: 'underline' } }}
            >
              About
            </Link>
            <Link
              component={RouterLink}
              to="/legal/terms"
              sx={{ fontSize: '0.72rem', color: 'text.secondary', textDecoration: 'none', '&:hover': { color: RED, textDecoration: 'underline' } }}
            >
              Terms
            </Link>
            <Link
              component={RouterLink}
              to="/legal/privacy"
              sx={{ fontSize: '0.72rem', color: 'text.secondary', textDecoration: 'none', '&:hover': { color: RED, textDecoration: 'underline' } }}
            >
              Privacy
            </Link>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', fontSize: '0.65rem', lineHeight: 1.2 }}>
            © 2026 pickpls · contact@pickpls.com
          </Typography>
        </Box>
      )}
    </Scrollbar>
  );

  return (
    <RootStyle collapsed={isCollapsed}>
      <Drawer
        open
        variant="permanent"
        PaperProps={{
          sx: {
            width: drawerWidth,
            bgcolor: 'background.default',
            borderRight: 'none',
            transition: (theme) => theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {renderContent}
      </Drawer>
    </RootStyle>
  );
}
